import { Store, createStore, combineReducers } from 'redux';
import { QuestionData, CategoryData } from './data';
import { UserAccount } from './account.service';

interface QuestionState {
    readonly loading: boolean;
    readonly categories: CategoryData[];
    readonly category: CategoryData | null;
    readonly recentlyAdded: QuestionData[];
    readonly browsed: QuestionData[];
    readonly searched: QuestionData[];
    readonly filtered: QuestionData[];
    readonly viewing: QuestionData | null;
}

const initialQuestionState: QuestionState = {
    loading: false,
    categories: [],
    category: null,
    recentlyAdded: [],
    browsed: [],
    searched: [],
    filtered: [],
    viewing: null,
};

export const GETTING_CATEGORIES = 'GettingCategories';
export const gettingCategoriesAction = () =>
    ({
        type: GETTING_CATEGORIES,
    } as const);

export const GOT_CATEGORIES = 'GotCategories';
export const gotCategoriesAction = (categories: CategoryData[]) =>
    ({
        type: GOT_CATEGORIES,
        categories,
    } as const);

export const GETTING_CATEGORY = 'GettingCategory';
export const gettingCategoryAction = () =>
    ({
        type: GETTING_CATEGORY,
    } as const);

export const GOT_CATEGORY = 'GotCategory';
export const gotCategoryAction = (category: CategoryData | null) =>
    ({
        type: GOT_CATEGORY,
        category,
    } as const);

export const GETTING_RECENTLY_ADDED = 'GettingRecentlyAdded';
export const gettingRecentlyAddedAction = () =>
    ({
        type: GETTING_RECENTLY_ADDED,
    } as const);

export const GOT_RECENTLY_ADDED = 'GotRecentlyAdded';
export const gotRecentlyAddedAction = (questions: QuestionData[]) =>
    ({
        type: GOT_RECENTLY_ADDED,
        questions,
    } as const);

export const GETTING_BROWSED = 'GettingBrowsed';
export const gettingBrowsedAction = () =>
    ({
        type: GETTING_BROWSED,
    } as const);

export const GOT_BROWSED = 'GotBrowsed';
export const gotBrowsedAction = (questions: QuestionData[]) =>
    ({
        type: GOT_BROWSED,
        questions,
    } as const);

export const SEARCHING_QUESTIONS = 'SearchingQuestions';
export const searchingQuestionsAction = () => ({ type: SEARCHING_QUESTIONS } as const);

export const SEARCHED_QUESTIONS = 'SearchedQuestions';
export const searchedQuestionsAction = (questions: QuestionData[]) =>
    ({
        type: SEARCHED_QUESTIONS,
        questions: questions,
    } as const);

export const FILTERING_QUESTIONS = 'FilteringQuestions';
export const filteringQuestionsAction = () => ({ type: FILTERING_QUESTIONS } as const);

export const FILTERED_QUESTIONS = 'FilteredQuestions';
export const filteredQuestionsAction = (questions: QuestionData[]) =>
    ({
        type: FILTERED_QUESTIONS,
        questions: questions,
    } as const);

export const GETTING_QUESTION = 'GettingQuestion';
export const gettingQuestionAction = () => ({ type: GETTING_QUESTION } as const);

export const GOT_QUESTION = 'GotQuestion';
export const gotQuestionAction = (question: QuestionData | null) =>
    ({
        type: GOT_QUESTION,
        question: question,
    } as const);

type QuestionsActions =
    | ReturnType<typeof gettingCategoriesAction>
    | ReturnType<typeof gotCategoriesAction>
    | ReturnType<typeof gettingCategoryAction>
    | ReturnType<typeof gotCategoryAction>
    | ReturnType<typeof gettingRecentlyAddedAction>
    | ReturnType<typeof gotRecentlyAddedAction>
    | ReturnType<typeof gettingBrowsedAction>
    | ReturnType<typeof gotBrowsedAction>
    | ReturnType<typeof searchingQuestionsAction>
    | ReturnType<typeof searchedQuestionsAction>
    | ReturnType<typeof filteringQuestionsAction>
    | ReturnType<typeof filteredQuestionsAction>
    | ReturnType<typeof gettingQuestionAction>
    | ReturnType<typeof gotQuestionAction>
    | ReturnType<typeof gettingAccountAction>
    | ReturnType<typeof gotAccountAction>;

const questionsReducer = (state = initialQuestionState, action: QuestionsActions) => {
    switch (action.type) {
        case GETTING_CATEGORIES: {
            return {
                ...state,
                categories: [],
                loading: true,
            };
        }
        case GOT_CATEGORIES: {
            return {
                ...state,
                categories: action.categories,
                loading: false,
            };
        }
        case GETTING_CATEGORY: {
            return {
                ...state,
                category: null,
                loading: true,
            };
        }
        case GOT_CATEGORY: {
            return {
                ...state,
                category: action.category,
                loading: false,
            };
        }
        case GETTING_RECENTLY_ADDED: {
            return {
                ...state,
                recentlyAdded: [],
                loading: true,
            };
        }
        case GOT_RECENTLY_ADDED: {
            return {
                ...state,
                recentlyAdded: action.questions,
                loading: false,
            };
        }
        case GETTING_BROWSED: {
            return {
                ...state,
                browsed: [],
                loading: true,
            };
        }
        case GOT_BROWSED: {
            return {
                ...state,
                browsed: action.questions,
                loading: false,
            };
        }
        case SEARCHING_QUESTIONS: {
            return {
                ...state,
                searched: [],
                loading: true,
            };
        }
        case SEARCHED_QUESTIONS: {
            return {
                ...state,
                searched: action.questions,
                loading: false,
            };
        }
        case FILTERING_QUESTIONS: {
            return {
                ...state,
                filtered: [],
                loading: true,
            };
        }
        case FILTERED_QUESTIONS: {
            return {
                ...state,
                filtered: action.questions,
                loading: false,
            };
        }
        case GETTING_QUESTION: {
            return {
                ...state,
                loading: true,
                viewing: null,
            };
        }
        case GOT_QUESTION: {
            return {
                ...state,
                loading: false,
                viewing: action.question,
            };
        }
    }
    return state;
};

////////////////////
// account

interface AccountState {
    readonly loading: boolean;
    readonly user: UserAccount | null;
}

const initialAccountState: AccountState = {
    loading: false,
    user: null,
};

export const GETTING_ACCOUNT = 'GettingAccount';
export const gettingAccountAction = () => ({ type: GETTING_ACCOUNT } as const);

export const GOT_ACCOUNT = 'GotAccount';
export const gotAccountAction = (user: UserAccount | null) =>
    ({
        type: GOT_ACCOUNT,
        user: user,
    } as const);

type AccountActions = ReturnType<typeof gettingAccountAction> | ReturnType<typeof gotAccountAction>;

const accountReducer = (state = initialAccountState, action: AccountActions) => {
    switch (action.type) {
        case GETTING_ACCOUNT: {
            return {
                ...state,
                loading: true,
                user: null,
            };
        }
        case GOT_ACCOUNT: {
            return {
                ...state,
                loading: false,
                user: action.user,
            };
        }
    }
    return state;
};

export interface AppState {
    readonly questions: QuestionState;
    readonly account: AccountState;
}

const rootReducer = combineReducers<AppState>({ questions: questionsReducer, account: accountReducer });
export const configureStore = (): Store<AppState> => {
    const store = createStore(rootReducer, undefined);
    return store;
};
